import { CaseLawGavel } from '@/assets/icons'
import * as Switch from '@radix-ui/react-switch'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { MenuContent, MenuSeparator } from '@/components/menu/MenuContent'
import { useCallback, useEffect, useState } from 'react'
import SourceDialog from '../sources-dialog/SourceDialog'
import {
  selectAgentConversationFormHasJurisdictionFilters,
  selectAgentConversationFormResearchEnabled,
  selectDefaultResearchJurisdictionForConversation,
} from '../store/selectors'
import { researchFiltersActions } from '../sources-dialog/store/slice'
import { selectResearchFiltersDisplay, selectResearchFiltersForConversation } from '../sources-dialog/store/selectors'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { RiaToggleState, RiaToggleName, RiaDialogSource } from '@/analytics/enums/ria.enums'
import { AgentConversationFormActions } from '../store/slice'
import { selectAgentConversationDetailLoaded } from '@/agent/conversations/store/selectors'
import { useGetAllCourtsQuery } from '@/store/apis/hosted-filters-api'

type SourcesControlsProps = {
  conversationId: string
}

// Distance in pixels to position dropdown above the textarea to avoid overlap
const SOURCES_DROPDOWN_OFFSET_PX = 120

function SourcesControls(props: SourcesControlsProps) {
  const { conversationId } = props
  const dispatch = useAppDispatch()
  const [isSourceDialogOpen, setIsSourceDialogOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const { trackEvent } = useAnalytics()

  const openSourcesDialogOnMount = useAppSelector((state: RootState) => state.agentConversationFormState.openSourcesDialogOnMount)
  const formExists = useAppSelector((state: RootState) => Boolean(state.agentConversationFormState.forms[conversationId]))
  const isResearchEnabled = useAppSelector((state: RootState) => selectAgentConversationFormResearchEnabled(state, { conversationId }))
  const hasJurisdictionFilters = useAppSelector((state: RootState) => selectAgentConversationFormHasJurisdictionFilters(state, { conversationId }))
  const conversationDetailLoaded = useAppSelector((state: RootState) => selectAgentConversationDetailLoaded(state, { conversationId }))
  const defaultResearchJurisdiction = useAppSelector((state: RootState) => selectDefaultResearchJurisdictionForConversation(state, { conversationId }))

  useGetAllCourtsQuery(undefined, { skip: !isResearchEnabled })

  const prefillDefaultResearchSources = useCallback(() => {
    if (!formExists) return
    if (!conversationDetailLoaded) return
    if (hasJurisdictionFilters) return
    if (!defaultResearchJurisdiction) return

    dispatch(
      researchFiltersActions.prefillFromJurisdiction({
        conversationId,
        jurisdiction: defaultResearchJurisdiction,
      })
    )
  }, [conversationDetailLoaded, conversationId, defaultResearchJurisdiction, dispatch, formExists, hasJurisdictionFilters])

  useEffect(() => {
    prefillDefaultResearchSources()
  }, [prefillDefaultResearchSources])

  // Auto-open sources dialog when redirected from the dashboard via "Conduct Legal Research"
  useEffect(() => {
    if (!openSourcesDialogOnMount) return

    prefillDefaultResearchSources()
    setIsSourceDialogOpen(true)
    dispatch(AgentConversationFormActions.setOpenSourcesDialogOnMount(false))
  }, [dispatch, openSourcesDialogOnMount, prefillDefaultResearchSources])

  // Select description for selected sources from filters UI state
  const selectedSourcesDescription = useAppSelector((state: RootState) => selectResearchFiltersDisplay(state, { conversationId }))

  // Select research filters for restoring selections when toggling ON
  const researchFilters = useAppSelector((state: RootState) => selectResearchFiltersForConversation(state, { conversationId }))

  const handleSourceDialogOpenChange = (open: boolean) => {
    if (open) {
      prefillDefaultResearchSources()
    }

    setIsSourceDialogOpen(open)
    if (open) {
      setIsDropdownOpen(false)
      trackEvent(AnalyticsEvent.RiaSourcesDialogOpened, {
        riaDialogSource: RiaDialogSource.KnowledgeSourcesDropdown,
        conversationId,
      })
    }
  }

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newState = isResearchEnabled ? RiaToggleState.Off : RiaToggleState.On

    trackEvent(AnalyticsEvent.RiaToggleClicked, {
      riaToggleState: newState,
      riaToggleName: RiaToggleName.LegalResearch,
      conversationId,
    })

    if (newState === RiaToggleState.Off) {
      dispatch(AgentConversationFormActions.clearResearchJurisdictions({ conversationId }))
    } else {
      if (researchFilters) {
        dispatch(
          AgentConversationFormActions.syncResearchFiltersToRequestParams({
            conversationId,
            stateLrrSources: researchFilters.stateLrrSources,
            stateCaselawSources: researchFilters.stateCaselawSources,
            federalLrrSources: researchFilters.federalLrrSources,
            federalCaselawSources: researchFilters.federalCaselawSources,
          })
        )
      }
      handleSourceDialogOpenChange(true)
    }
  }

  const handleDropdownOpenChange = (open: boolean) => {
    setIsDropdownOpen(open)
    if (open) {
      trackEvent(AnalyticsEvent.RiaSourcesButtonClicked, {
        conversationId,
      })
    }
  }

  return (
    <>
      <SourceDialog open={isSourceDialogOpen} onOpenChange={handleSourceDialogOpenChange} conversationId={conversationId} />
      <DropdownMenu.Root open={isDropdownOpen} onOpenChange={handleDropdownOpenChange}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className={`font-semibold rounded-md border pl-2.5 pr-3 py-1.5 text-sm transition-all duration-300 flex items-center gap-2 max-h-8
             ${
               isResearchEnabled
                 ? 'bg-brand-50 border-brand-200 border-6px text-brand-500 hocus:bg-brand-100'
                 : 'border-brand-neutral-300 text-brand-neutral-600 hocus:bg-brand-neutral-100'
             }`}
            aria-label="Open sources DropdownMenu"
          >
            <CaseLawGavel className="w-5 h-5" />
            <span className="text-sm font-semibold">Legal Research</span>
          </button>
        </DropdownMenu.Trigger>
        <MenuContent className="fixed w-[calc(100vw-40px)] max-w-[348px] border-0" side="top" sideOffset={SOURCES_DROPDOWN_OFFSET_PX} align="start">
          <DropdownMenu.Label className="px-4 py-2 text-sm font-medium text-brand-neutral-500 bg-brand-neutral-50">Knowledge Sources</DropdownMenu.Label>
          <MenuSeparator />
          <div className="m-2 hover:bg-brand-neutral-100 rounded cursor-pointer transition-colors" onClick={handleToggleClick}>
            <div className="flex items-center justify-between gap-2 text-brand-neutral-700 text-sm font-medium px-2 pt-2 pb-1">
              <div className="flex items-center gap-2">
                <span>Legal Research</span>
              </div>
              <div className="flex items-center" onClick={handleToggleClick}>
                <Switch.Root
                  className="relative h-[25px] w-[42px] cursor-default rounded-full bg-brand-neutral-400 outline-none data-[state=checked]:bg-brand-500 transition-all duration-300"
                  id="legal-research-switch"
                  checked={isResearchEnabled}
                >
                  <Switch.Thumb className="block size-[21px] translate-x-0.5 rounded-full bg-white transition-transform duration-100 will-change-transform data-[state=checked]:translate-x-[19px]" />
                </Switch.Root>
              </div>
            </div>
            {isResearchEnabled ? (
              <div className="flex items-center justify-start gap-2 text-brand-neutral-600 text-xs font-medium pt-0 pb-2 px-2">
                <span>{selectedSourcesDescription}</span>
                <button
                  type="button"
                  className="text-brand-500 text-xs font-medium underline"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleSourceDialogOpenChange(true)
                  }}
                >
                  Modify
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2 text-brand-neutral-600 text-xs font-medium pt-0 pb-2 px-2">
                <span>Case law, statutory, and regulatory authority</span>
              </div>
            )}
          </div>
        </MenuContent>
      </DropdownMenu.Root>
    </>
  )
}

export default SourcesControls
